<template>
  <div class="upi-qr-container">
    <!-- QR Code Display -->
    <div class="qr-code-wrapper">
      <div 
        ref="qrContainer" 
        class="qr-code-display"
        :class="{ 'qr-loading': isGenerating }"
      >
        <div v-if="isGenerating" class="loading-spinner">
          <div class="animate-spin w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
        <div v-else-if="qrError" class="error-state">
          <svg class="w-8 h-8 text-red-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.064 16.5c-.77.833.192 2.5 1.732 2.5z"/>
          </svg>
          <p class="text-xs text-red-600">QR Error</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'

// Props
const props = defineProps({
  vpa: {
    type: String,
    required: true,
    validator: (value) => {
      // Basic UPI VPA validation
      const upiRegex = /^[a-zA-Z0-9.\-_]{2,49}@[a-zA-Z0-9][a-zA-Z0-9.-]*$/
      return upiRegex.test(value)
    }
  },
  amount: {
    type: [Number, String],
    required: true,
    validator: (value) => {
      const num = parseFloat(value)
      return !isNaN(num) && num > 0
    }
  },
  merchantName: {
    type: String,
    default: ''
  },
  transactionNote: {
    type: String,
    default: 'Payment for purchase'
  },
  transactionRef: {
    type: String,
    default: ''
  },
  qrSize: {
    type: Number,
    default: 256,
    validator: (value) => value >= 128 && value <= 512
  }
})

// Reactive data
const qrContainer = ref(null)
const isGenerating = ref(false)
const qrError = ref('')
const isMounted = ref(false)

// Computed UPI URL
const upiUrl = computed(() => {
  const params = new URLSearchParams({
    pa: props.vpa,
    am: parseFloat(props.amount).toFixed(2),
    cu: 'INR',
    tn: props.transactionNote
  })
  
  if (props.merchantName) {
    params.append('pn', props.merchantName)
  }
  
  if (props.transactionRef) {
    params.append('tr', props.transactionRef)
  }
  
  return `upi://pay?${params.toString()}`
})

// Generate QR Code
const generateQRCode = async () => {
  // Check if component is still mounted and DOM ref exists
  if (!isMounted.value || !qrContainer.value) {
    console.warn('QR container not available or component unmounted')
    return
  }
  
  isGenerating.value = true
  qrError.value = ''
  
  try {
    // Use Vue's safer approach - set innerHTML to empty instead of manual child removal
    if (qrContainer.value && isMounted.value) {
      qrContainer.value.innerHTML = ''
    }
    
    // Dynamic import of QRCode library
    const QRCode = (await import('qrcode')).default
    
    // Generate QR code
    const canvas = document.createElement('canvas')
    await QRCode.toCanvas(canvas, upiUrl.value, {
      width: props.qrSize,
      margin: 2,
      color: {
        dark: '#1F2937',  // Dark gray
        light: '#FFFFFF'  // White
      },
      errorCorrectionLevel: 'M'
    })
    
    // Check container still exists and component is mounted before appending
    if (qrContainer.value && isMounted.value) {
      qrContainer.value.appendChild(canvas)
    }
    
  } catch (error) {
    console.error('QR Code generation failed:', error)
    if (isMounted.value) {
      qrError.value = 'Failed to generate QR code. Please check the details and try again.'
    }
  } finally {
    if (isMounted.value) {
      isGenerating.value = false
    }
  }
}

// Watch for changes that should trigger QR regeneration
watch([() => props.vpa, () => props.amount, () => props.merchantName, () => props.transactionNote, () => props.transactionRef], async () => {
  if (isMounted.value) {
    await nextTick()
    generateQRCode()
  }
})

// Generate QR code on mount
onMounted(async () => {
  isMounted.value = true
  await nextTick()
  generateQRCode()
})

// Clean up on unmount
onUnmounted(() => {
  isMounted.value = false
  if (qrContainer.value) {
    qrContainer.value.innerHTML = ''
  }
})
</script>

<style scoped>
.upi-qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
}

.qr-code-wrapper {
  position: relative;
}

.qr-code-display {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  min-height: 200px;
  min-width: 200px;
}

.qr-code-display.qr-loading {
  background-color: #f3f4f6;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.error-state {
  text-align: center;
}
</style>